import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Rights, User, useUsersQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import { FormikErrors, useFormikContext } from 'formik';
import ProfileAvatar from '../profiles/ProfileAvatar';
import UserListOption from './UserListOption';
import UserListButtonOption from './UserListButtonOption';
import { getUserProfileFullName } from '../../utils/users';

export interface Props {
  id: string;
  disabled: boolean;
}

interface ManagerFormValues {
  manager: Partial<User> | null;
}

export const IsoManagerAutocomplete = ({ id, disabled }: Props) => {
  const { errors, touched, values, handleBlur, setFieldValue } = useFormikContext<ManagerFormValues>();
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.manager?.id);
  useEffect(() => {
    setShowInfo(disabled || !!values?.manager?.id);
  }, [disabled, values?.manager?.id]);
  const inputReference = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<readonly User[]>([]);
  const [userInputValue, setUserInputValue] = useState<string>('');
  const {
    loading: usersLoading,
    data: usersData,
    refetch: usersRefetch
  } = useUsersQuery({
    variables: { right: Rights.IsIsoManager, isActive: true },
    fetchPolicy: 'network-only'
  });
  const usersFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          usersRefetch({ search: request.input }).then((rq) => setUsers((rq.data.users.data as User[]) || [])),
        200
      ),
    [usersRefetch]
  );
  useEffect(() => setUsers((usersData?.users.data as User[]) || []), [usersData?.users.data, usersLoading]);
  useEffect(() => {
    usersFetch({ input: userInputValue });
  }, [userInputValue, usersFetch]);

  return (
    <>
      {values?.manager?.id && showInfo && (
        <Stack direction={'row'}>
          <UserListButtonOption
            user={values.manager as User}
            type="ISO Manager Name"
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.manager?.id);
          }}
        >
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Partial<User>) => getUserProfileFullName(option as User)}
            options={users}
            autoComplete
            value={values?.manager?.id ? (values.manager as User) : null}
            isOptionEqualToValue={(option: User, value: User) =>
              !!value?.id && option.id === value.id
            }            
            onChange={(event: SyntheticEvent, newValue: User | null) => {
              setFieldValue('manager', newValue || null);
              setShowInfo(!!newValue?.id);
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setUserInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={'Select ISO Manager'}
                inputRef={inputReference}
                name="manager"
                onBlur={handleBlur}
                error={Boolean(touched.manager && (errors.manager as FormikErrors<Partial<User>>)?.id)}
                helperText={touched.manager && (errors.manager as FormikErrors<Partial<User>>)?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.manager?.id ? <ProfileAvatar user={values?.manager as User} size={'sm'} /> : null
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return <UserListOption key={key} user={option as User} {...rest} />;
            }}
          />
        </ClickAwayListener>
      )}
    </>
  );
};
